/**
 * Walking with the guard up (lane C0-POSE-FIX): the guard's upper body (strikes.ts `block`: weapon or fists up
 * before the body, weight back, leaning away a little) carried on a shuffle step (滑步 / 跟步), the front foot sliding
 * ahead and the back foot following, so the feet never cross and the stance never closes while the guard is held.
 *
 * Eight key poses round one loop, blended on the Catmull-Rom spline (motions/spline.ts) like every motion. Each key
 * is a sample of a two-foot gait (`gait`, kit.ts) with a short reach, three quarters of the cycle planted, about
 * the block's own foot spacing: the keys are whole skeletons, so the poses survive a change of body proportions.
 * The renderer plays it forward when stepping toward the way the figure faces and backward when giving ground
 * (render/guardWalk.ts).
 */
import { gait, type MotionDef, type PoseRigs } from './kit';

/** Keys round the loop. */
const KEYS = 8;
/**
 * Centre of each foot's slide, a little closer than the block's feet (strikes.ts `block`: −7, 4) so the stride stays
 * inside the walk's own span (±6) and a long skirt still covers the legs.
 */
const BACK = -5;
const FRONT = 3;

/** Key `i` of the loop: the block's body on the shuffle's legs. */
function guardStep(i: number) {
  const t = i / KEYS;
  // Short reach, long plant, a low lift (a foot skims the ground in a guarded step), little bob.
  const g = gait(t, 2, 0.75, 1.5, 1, 1);
  // The weight rides forward as the front foot lands and back again as the rear foot follows.
  const shift = Math.sin(t * Math.PI * 2);
  return {
    o: 3 + g.o,
    lean: -1,
    tilt: -5 + Math.round(shift),
    f1: Math.round(BACK + g.f1),
    f2: Math.round(FRONT + g.f2),
    l1: g.l1,
    l2: g.l2,
    sway: 0.4 * shift,
    arm: 'guard' as const,
    // The free hand's sword finger forward, never hanging (motions-polish: block; motions/wushu.ts).
    far: 'fwd' as const,
    face: 'block' as const,
  };
}

export type Poses = 'gw0' | 'gw1' | 'gw2' | 'gw3' | 'gw4' | 'gw5' | 'gw6' | 'gw7';

export const POSES: PoseRigs<Poses> = {
  gw0: () => guardStep(0),
  gw1: () => guardStep(1),
  gw2: () => guardStep(2),
  gw3: () => guardStep(3),
  gw4: () => guardStep(4),
  gw5: () => guardStep(5),
  gw6: () => guardStep(6),
  gw7: () => guardStep(7),
};

export const MOTIONS = [
  { id: 'guardWalk', keys: ['gw0', 'gw1', 'gw2', 'gw3', 'gw4', 'gw5', 'gw6', 'gw7'], loop: true },
] as const satisfies readonly MotionDef[];
